import {cssLanguage} from "@codemirror/lang-css"
import {tokenRule} from "./tokens"

// Parse for positions, but edit only the insertion point. Re-serializing CSSOM
// would discard the user's comments, formatting, and unsupported declarations.
export function insertToken(source, token) {
  const sheet = new CSSStyleSheet()
  const headers = new Map()
  const normalize = header => {
    if (!headers.has(header)) {
      try {
        sheet.replaceSync(`${header} {}`)
        const rule = sheet.cssRules[0]
        headers.set(header, rule?.selectorText || rule?.cssText.slice(0, rule.cssText.lastIndexOf('{')).trim() || header.trim())
      } catch (_) { headers.set(header, header.trim()) }
    }
    return headers.get(header)
  }
  const selector = normalize(token.selector)
  const conditions = token.conditions.map(normalize)
  const matches = []
  let existing = null

  cssLanguage.parser.parse(source).iterate({enter: cursor => {
    if (cursor.name !== 'RuleSet') return
    const rule = cursor.node
    const block = rule.getChild('Block')
    if (!block || block.lastChild?.name !== '}') return
    if (normalize(source.slice(rule.from, block.from)) !== selector) return
    const context = []
    for (let parent = rule.parent; parent; parent = parent.parent) {
      // A nested selector has an implicit parent; don't confuse it with the
      // same selector at document level.
      if (parent.name === 'RuleSet') return
      if (parent.name === 'Block' && parent.parent) {
        context.unshift(normalize(source.slice(parent.parent.from, parent.from)))
      }
    }
    if (context.length !== conditions.length || context.some((header, i) => header !== conditions[i])) return
    matches.push(block)
    for (const declaration of block.getChildren('Declaration')) {
      const name = declaration.getChild('VariableName')
      if (name && declaration.getChild(':') && source.slice(name.from, name.to) === token.name) existing = declaration
    }
  }})

  if (existing) {
    const colon = existing.getChild(':')
    const end = existing.getChild('Important')?.from ?? existing.to
    const raw = source.slice(colon.to, end)
    const from = colon.to + raw.length - raw.trimStart().length
    // Re-inserting an existing token selects its current value rather than
    // resetting a customization or losing the original Mogno comment.
    return {source, from, to: end - (raw.length - raw.trimEnd().length)}
  }

  const block = matches.at(-1)
  if (block) {
    const close = block.lastChild.from
    const body = source.slice(block.from + 1, close)
    const end = close - (body.length - body.trimEnd().length)
    const ruleLine = source.slice(source.lastIndexOf('\n', block.from) + 1, block.from)
    const indent = /^\s*/.exec(ruleLine)[0]
    const first = block.getChild('Declaration')
    const firstLine = first && source.slice(source.lastIndexOf('\n', first.from) + 1, first.from)
    const propertyIndent = firstLine && /^[\t ]+$/.test(firstLine) ? firstLine : indent + '  '
    const last = block.getChildren('Declaration').at(-1)
    let prefix = source.slice(0, end)
    // CSS allows the final semicolon to be omitted. Add it before any trailing
    // comment, so the new token remains a separate declaration.
    if (last && last.nextSibling?.name !== ';') prefix = prefix.slice(0, last.to) + ';' + prefix.slice(last.to)
    const addition = `\n${propertyIndent}${token.name}: ${token.value};\n${indent}`
    const from = prefix.length + addition.indexOf(': ', addition.indexOf(token.name)) + 2
    return {source: prefix + addition + source.slice(close), from, to: from + token.value.length}
  }

  const prefix = source + (source.trim() ? '\n\n' : '')
  const addition = tokenRule(token) + '\n'
  const from = prefix.length + addition.indexOf(`${token.name}: `) + token.name.length + 2
  return {source: prefix + addition, from, to: from + token.value.length}
}
